import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { CompetencyWithEvaluation } from '../../types/GrowthWheel'
import CompetencyAreaViewCard from '../../components/GrowthWheelComponents/CompetencyAreaViewCard'
import CompetencyViewCard from '../../components/GrowthWheelComponents/CompetencyViewCard'
import {
  getCompletedCompetencies,
  getListTransitionClass,
  getPageTransitionClass,
  getTotalCompetencies,
} from '../../utils/growth-wheel-utils'
import { useGrowthWheelPagination } from '../../hooks/useGrowthWheelPagination'
import GrowthWheelPagination from '../../components/GrowthWheelPagination/GrowthWheelPagination'
import { useContentTransition } from '../../hooks/useContentTransition'
import BackButton from '../../components/GrowthWheelComponents/BackButton'
import {
  setCurrentCompetencyArea,
  setCurrentCompetency,
} from '../../redux/growthWheel.slice'
import { RootState } from '../../redux/store'

interface Props {
  userName?: string
  guildName?: string
}

const GrowthWheelCompetencyAreaView = ({ userName, guildName }: Props) => {
  const dispatch = useDispatch()
  const currentCompetencyArea = useSelector(
    (state: RootState) => state.growthWheel.currentCompetencyArea,
  )
  const userCompetencyAreasList = useSelector(
    (state: RootState) => state.growthWheel.userCompetencyAreasData,
  )

  const handleClickAreaChange = (
    competencyArea: Parameters<typeof setCurrentCompetencyArea>[0],
  ) => {
    dispatch(setCurrentCompetencyArea(competencyArea))
  }

  const handleClickCompetencyChange = (
    competency: CompetencyWithEvaluation | null,
  ) => {
    dispatch(setCurrentCompetency(competency))
  }

  if (!currentCompetencyArea) return null
  const competencies = currentCompetencyArea.competencies ?? []
  const totalCompetencies = getTotalCompetencies(competencies)
  const completedCompetencies = getCompletedCompetencies(competencies)
  const {
    currentIndex: currentAreaIndex,
    handlePrevious,
    handleNext,
  } = useGrowthWheelPagination(
    userCompetencyAreasList,
    currentCompetencyArea,
    handleClickAreaChange,
  )
  const { isTransitioning, transitionDirection } =
    useContentTransition(currentAreaIndex)

  const getCompetencyCardProps = (competency: CompetencyWithEvaluation) => {
    const isFocus = competency.evaluation?.focus ?? false
    const showSelfAssessed = Boolean(competency.evaluation?.selfLevel)
    const showGuided = Boolean(competency.evaluation?.leaderLevel)
    const selfLevel = competency.evaluation?.selfLevel ?? null
    const leaderLevel = competency.evaluation?.leaderLevel ?? null
    const status = showSelfAssessed || showGuided

    return {
      isFocus,
      status,
      showSelfAssessed,
      showGuided,
      selfLevel,
      leaderLevel,
    }
  }

  return (
    <div className="container mb-5 gw-competency-area-view">
      {userName ? (
        <div className="gw-competency-area-view-header">
          <BackButton
            onClick={() => handleClickAreaChange(null)}
            text="Back to All Competencies"
          />
          <div className="gw-growth-wheel-header-titles">
            <h1 className="gw-growth-wheel-title">
              {`${userName}'s`} Growth Wheel
            </h1>
            {guildName ? (
              <p className="gw-growth-wheel-guild-name">{guildName}</p>
            ) : null}
          </div>
        </div>
      ) : (
        <BackButton
          onClick={() => handleClickAreaChange(null)}
          text="Back to All Competencies"
        />
      )}

      <div
        className={`gw-competency-area-view-content gw-competency-area-page ${getPageTransitionClass(
          isTransitioning,
          transitionDirection,
        )}`}
      >
        <CompetencyAreaViewCard
          title={currentCompetencyArea.name}
          description={currentCompetencyArea.description}
          completed={completedCompetencies}
          total={totalCompetencies}
        />

        <div className="gw-competency-area-competencies">
          <h3 className="gw-competency-area-competencies-title">
            Sub-competencies
          </h3>
          <div
            className={`gw-competency-area-competencies-list ${getListTransitionClass(
              isTransitioning,
              transitionDirection,
            )}`}
          >
            {competencies.map(competency => {
              return (
                <CompetencyViewCard
                  key={competency.id}
                  title={competency.name}
                  description={competency.description}
                  evaluation={getCompetencyCardProps(competency)}
                  onClick={() => handleClickCompetencyChange(competency)}
                />
              )
            })}
          </div>
        </div>
      </div>

      <GrowthWheelPagination
        currentIndex={currentAreaIndex}
        optionsList={userCompetencyAreasList}
        handleClickPrevious={handlePrevious}
        handleClickNext={handleNext}
        type="Competency"
      />
    </div>
  )
}

export default GrowthWheelCompetencyAreaView
